import { generateIdempotencyKey } from './idempotency';

const PENDING_ATTEMPT_VERSION = 1;
const PENDING_ATTEMPT_STORAGE_PREFIX = 'videovector:pending-idempotency';

// Match the longest canonical backend idempotency/operation receipt retention.
export const DEFAULT_PENDING_ATTEMPT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_PENDING_ATTEMPTS_PER_OPERATION = 32;
// Backend one-time credential receipts expire after one hour. Keep the browser
// owner inside that replay window so an expired receipt never blocks retries.
export const ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS = 55 * 60 * 1000;

export class PendingIdempotencyAttemptCapacityError extends Error {
  readonly code = 'pending_idempotency_attempt_capacity_exceeded';

  constructor() {
    super(
      'Too many unresolved attempts are pending for this tool. '
      + 'Retry an existing request or wait for an older attempt to expire.',
    );
    this.name = 'PendingIdempotencyAttemptCapacityError';
  }
}

export class PendingIdempotencyAttemptIdentityConflictError extends Error {
  readonly code = 'pending_idempotency_attempt_identity_conflict';

  constructor() {
    super(
      'The explicit idempotency key conflicts with an unresolved attempt for '
      + 'the same request.',
    );
    this.name = 'PendingIdempotencyAttemptIdentityConflictError';
  }
}

export class PendingIdempotencyAttemptStorageUnavailableError extends Error {
  readonly code = 'pending_idempotency_attempt_storage_unavailable';

  constructor() {
    super(
      'Durable retry protection is unavailable in this browser session. '
      + 'No tool operation was dispatched.',
    );
    this.name = 'PendingIdempotencyAttemptStorageUnavailableError';
  }
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & Partial<
Pick<Storage, 'key' | 'length'>
>;

export interface PendingIdempotencyAttempt {
  operation: string;
  requestFingerprint: string | null;
  idempotencyKey: string;
  expiresAtMs: number;
}

interface PersistedPendingIdempotencyAttempt {
  version: number;
  operation: string;
  requestFingerprint: string;
  idempotencyKey: string;
  expiresAtMs: number;
}

interface PendingAttemptDependencies {
  storage?: StorageLike | null;
  now?: () => number;
  generateKey?: (prefix: string) => string;
  fingerprintRequest?: (
    operation: string,
    requestPayload: unknown,
  ) => Promise<string | null>;
}

interface GetOrCreatePendingAttemptOptions extends PendingAttemptDependencies {
  namespace: string;
  operation: string;
  requestPayload: unknown;
  keyPrefix: string;
  preferredIdempotencyKey?: string;
  inMemoryAttempt?: PendingIdempotencyAttempt;
  ttlMs?: number;
  maxAttemptsPerOperation?: number;
}

interface FindPendingAttemptOptions extends PendingAttemptDependencies {
  namespace: string;
  operation: string;
  requestPayload: unknown;
  inMemoryAttempt?: PendingIdempotencyAttempt;
}

interface ClearPendingAttemptOptions extends Pick<
PendingAttemptDependencies,
'storage'
> {
  namespace: string;
  operation: string;
  requestFingerprint: string | null;
  expectedIdempotencyKey: string;
}

const normalizeRequiredIdentifier = (value: string, name: string): string => {
  const normalized = value.trim();
  if (!normalized) {
    throw new Error(`${name} is required`);
  }
  return normalized;
};

const getStorage = (
  providedStorage: StorageLike | null | undefined,
): StorageLike | null => {
  if (providedStorage !== undefined) {
    return providedStorage;
  }
  try {
    return globalThis.sessionStorage || null;
  } catch {
    return null;
  }
};

const buildOperationStoragePrefix = (
  namespace: string,
  operation: string,
): string => (
  `${PENDING_ATTEMPT_STORAGE_PREFIX}:${encodeURIComponent(namespace)}:${encodeURIComponent(operation)}:`
);

const buildAttemptStorageKey = (
  namespace: string,
  operation: string,
  requestFingerprint: string,
): string => (
  `${buildOperationStoragePrefix(namespace, operation)}${encodeURIComponent(requestFingerprint)}`
);

const canonicalizeJsonValue = (
  value: unknown,
  ancestors: Set<object>,
): unknown => {
  if (
    value === null
    || typeof value === 'string'
    || typeof value === 'boolean'
  ) {
    return value;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (
    typeof value === 'undefined'
    || typeof value === 'function'
    || typeof value === 'symbol'
  ) {
    return undefined;
  }
  if (typeof value === 'bigint') {
    throw new TypeError('BigInt values are not JSON serializable');
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (ancestors.has(value)) {
    throw new TypeError('Cyclic values are not JSON serializable');
  }

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => canonicalizeJsonValue(item, ancestors) ?? null);
    }
    const record = value as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};
    Object.keys(record).sort().forEach((key) => {
      const item = canonicalizeJsonValue(record[key], ancestors);
      if (item !== undefined) {
        normalized[key] = item;
      }
    });
    return normalized;
  } finally {
    ancestors.delete(value);
  }
};

const bytesToHex = (bytes: Uint8Array): string => (
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
);

export const fingerprintPendingIdempotencyRequest = async (
  operation: string,
  requestPayload: unknown,
): Promise<string | null> => {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle || typeof TextEncoder === 'undefined') {
      return null;
    }
    const canonicalPayload = JSON.stringify({
      operation,
      requestPayload: canonicalizeJsonValue(requestPayload, new Set<object>()),
    });
    const digest = await subtle.digest(
      'SHA-256',
      new TextEncoder().encode(canonicalPayload),
    );
    return `sha256:${bytesToHex(new Uint8Array(digest))}`;
  } catch {
    // Without a trustworthy fingerprint, never persist an argument binding.
    return null;
  }
};

const parsePersistedAttempt = (
  raw: string,
  operation: string,
  expectedFingerprint: string,
  nowMs: number,
): PersistedPendingIdempotencyAttempt | null => {
  let parsed: Partial<PersistedPendingIdempotencyAttempt>;
  try {
    parsed = JSON.parse(raw) as Partial<PersistedPendingIdempotencyAttempt>;
  } catch {
    throw new PendingIdempotencyAttemptStorageUnavailableError();
  }
  if (
    parsed.version !== PENDING_ATTEMPT_VERSION
    || parsed.operation !== operation
    || parsed.requestFingerprint !== expectedFingerprint
    || !/^sha256:[0-9a-f]{64}$/.test(parsed.requestFingerprint)
    || typeof parsed.idempotencyKey !== 'string'
    || !parsed.idempotencyKey.trim()
    || parsed.idempotencyKey !== parsed.idempotencyKey.trim()
    || parsed.idempotencyKey.length > 256
    || typeof parsed.expiresAtMs !== 'number'
    || !Number.isFinite(parsed.expiresAtMs)
  ) {
    throw new PendingIdempotencyAttemptStorageUnavailableError();
  }
  if (parsed.expiresAtMs <= nowMs) {
    return null;
  }
  return parsed as PersistedPendingIdempotencyAttempt;
};

const removeStorageEntry = (
  storage: StorageLike | null,
  storageKey: string,
): void => {
  try {
    storage?.removeItem(storageKey);
  } catch {
    // Storage denial must not block the user action.
  }
};

const listOperationStorageKeys = (
  storage: StorageLike,
  namespace: string,
  operation: string,
): string[] => {
  try {
    if (
      typeof storage.key !== 'function'
      || typeof storage.length !== 'number'
    ) {
      throw new PendingIdempotencyAttemptStorageUnavailableError();
    }
    const prefix = buildOperationStoragePrefix(namespace, operation);
    const keys: string[] = [];
    const storageLength = storage.length;
    for (let index = 0; index < storageLength; index += 1) {
      const candidate = storage.key(index);
      if (candidate?.startsWith(prefix)) {
        keys.push(candidate);
      }
    }
    return keys;
  } catch {
    throw new PendingIdempotencyAttemptStorageUnavailableError();
  }
};

const fingerprintFromStorageKey = (
  storageKey: string,
  namespace: string,
  operation: string,
): string | null => {
  const prefix = buildOperationStoragePrefix(namespace, operation);
  if (!storageKey.startsWith(prefix)) {
    return null;
  }
  try {
    const fingerprint = decodeURIComponent(storageKey.slice(prefix.length));
    return /^sha256:[0-9a-f]{64}$/.test(fingerprint) ? fingerprint : null;
  } catch {
    return null;
  }
};

const cleanupOperationAttempts = (
  storage: StorageLike,
  namespace: string,
  operation: string,
  nowMs: number,
): PersistedPendingIdempotencyAttempt[] => {
  const liveAttempts: Array<{
    attempt: PersistedPendingIdempotencyAttempt;
  }> = [];
  listOperationStorageKeys(storage, namespace, operation).forEach((storageKey) => {
    const fingerprint = fingerprintFromStorageKey(
      storageKey,
      namespace,
      operation,
    );
    try {
      const raw = storage.getItem(storageKey);
      const attempt = fingerprint && raw
        ? parsePersistedAttempt(raw, operation, fingerprint, nowMs)
        : null;
      if (!attempt) {
        removeStorageEntry(storage, storageKey);
        return;
      }
      liveAttempts.push({
        attempt,
      });
    } catch {
      // An unreadable ownership row cannot be replaced safely.
      throw new PendingIdempotencyAttemptStorageUnavailableError();
    }
  });
  return liveAttempts.map(({ attempt }) => attempt);
};

export const getOrCreatePendingIdempotencyAttempt = async ({
  namespace,
  operation,
  requestPayload,
  keyPrefix,
  preferredIdempotencyKey,
  inMemoryAttempt,
  ttlMs = DEFAULT_PENDING_ATTEMPT_TTL_MS,
  maxAttemptsPerOperation = MAX_PENDING_ATTEMPTS_PER_OPERATION,
  storage: providedStorage,
  now = Date.now,
  generateKey = generateIdempotencyKey,
  fingerprintRequest = fingerprintPendingIdempotencyRequest,
}: GetOrCreatePendingAttemptOptions): Promise<PendingIdempotencyAttempt> => {
  const normalizedNamespace = normalizeRequiredIdentifier(namespace, 'namespace');
  const normalizedOperation = normalizeRequiredIdentifier(operation, 'operation');
  const normalizedPrefix = normalizeRequiredIdentifier(keyPrefix, 'keyPrefix');
  const preferredKey = typeof preferredIdempotencyKey === 'string'
    ? preferredIdempotencyKey.trim()
    : '';
  if (preferredKey.length > 256) {
    throw new PendingIdempotencyAttemptIdentityConflictError();
  }
  const nowMs = now();
  const normalizedTtlMs = Number.isFinite(ttlMs) && ttlMs > 0
    ? ttlMs
    : DEFAULT_PENDING_ATTEMPT_TTL_MS;
  const normalizedMaxAttempts = (
    Number.isFinite(maxAttemptsPerOperation)
    && maxAttemptsPerOperation > 0
  )
    ? Math.max(1, Math.floor(maxAttemptsPerOperation))
    : MAX_PENDING_ATTEMPTS_PER_OPERATION;
  const expiresAtMs = nowMs + normalizedTtlMs;
  const requestFingerprint = await fingerprintRequest(
    normalizedOperation,
    requestPayload,
  ).catch(() => null);
  const storage = getStorage(providedStorage);

  const matchingLiveInMemoryAttempt = (
    inMemoryAttempt
    && inMemoryAttempt.operation === normalizedOperation
    && inMemoryAttempt.requestFingerprint === requestFingerprint
    && inMemoryAttempt.expiresAtMs > nowMs
  );
  if (
    matchingLiveInMemoryAttempt
    && preferredKey
    && preferredKey !== inMemoryAttempt.idempotencyKey
  ) {
    throw new PendingIdempotencyAttemptIdentityConflictError();
  }
  if (matchingLiveInMemoryAttempt) {
    return inMemoryAttempt;
  }

  if (!requestFingerprint || !storage) {
    throw new PendingIdempotencyAttemptStorageUnavailableError();
  }

  if (requestFingerprint && storage) {
    const liveAttempts = cleanupOperationAttempts(
      storage,
      normalizedNamespace,
      normalizedOperation,
      nowMs,
    );
    const storageKey = buildAttemptStorageKey(
      normalizedNamespace,
      normalizedOperation,
      requestFingerprint,
    );
    try {
      const raw = storage.getItem(storageKey);
      if (raw) {
        const persisted = parsePersistedAttempt(
          raw,
          normalizedOperation,
          requestFingerprint,
          nowMs,
        );
        if (persisted) {
          if (preferredKey && preferredKey !== persisted.idempotencyKey) {
            throw new PendingIdempotencyAttemptIdentityConflictError();
          }
          return persisted;
        }
        removeStorageEntry(storage, storageKey);
      }
    } catch (error) {
      if (error instanceof PendingIdempotencyAttemptIdentityConflictError) {
        throw error;
      }
      throw new PendingIdempotencyAttemptStorageUnavailableError();
    }
    if (liveAttempts.length >= normalizedMaxAttempts) {
      throw new PendingIdempotencyAttemptCapacityError();
    }
  }

  const attempt: PendingIdempotencyAttempt = {
    operation: normalizedOperation,
    requestFingerprint,
    idempotencyKey: preferredKey || generateKey(normalizedPrefix),
    expiresAtMs,
  };
  if (
    !attempt.idempotencyKey.trim()
    || attempt.idempotencyKey !== attempt.idempotencyKey.trim()
    || attempt.idempotencyKey.length > 256
  ) {
    throw new PendingIdempotencyAttemptIdentityConflictError();
  }
  if (requestFingerprint && storage) {
    const storageKey = buildAttemptStorageKey(
      normalizedNamespace,
      normalizedOperation,
      requestFingerprint,
    );
    const serializedAttempt = JSON.stringify({
        version: PENDING_ATTEMPT_VERSION,
        operation: normalizedOperation,
        requestFingerprint,
        idempotencyKey: attempt.idempotencyKey,
        expiresAtMs,
    });
    try {
      storage.setItem(storageKey, serializedAttempt);
      const confirmed = storage.getItem(storageKey);
      if (confirmed !== serializedAttempt) {
        throw new PendingIdempotencyAttemptStorageUnavailableError();
      }
    } catch {
      throw new PendingIdempotencyAttemptStorageUnavailableError();
    }
  }
  return attempt;
};

export const findPendingIdempotencyAttempt = async ({
  namespace,
  operation,
  requestPayload,
  inMemoryAttempt,
  storage: providedStorage,
  now = Date.now,
  fingerprintRequest = fingerprintPendingIdempotencyRequest,
}: FindPendingAttemptOptions): Promise<PendingIdempotencyAttempt | null> => {
  const normalizedNamespace = normalizeRequiredIdentifier(namespace, 'namespace');
  const normalizedOperation = normalizeRequiredIdentifier(operation, 'operation');
  const nowMs = now();
  const requestFingerprint = await fingerprintRequest(
    normalizedOperation,
    requestPayload,
  ).catch(() => null);
  if (
    inMemoryAttempt
    && inMemoryAttempt.operation === normalizedOperation
    && inMemoryAttempt.requestFingerprint === requestFingerprint
    && inMemoryAttempt.expiresAtMs > nowMs
  ) {
    return inMemoryAttempt;
  }

  const storage = getStorage(providedStorage);
  if (!requestFingerprint || !storage) {
    return null;
  }
  cleanupOperationAttempts(
    storage,
    normalizedNamespace,
    normalizedOperation,
    nowMs,
  );
  const storageKey = buildAttemptStorageKey(
    normalizedNamespace,
    normalizedOperation,
    requestFingerprint,
  );
  try {
    const raw = storage.getItem(storageKey);
    return raw
      ? parsePersistedAttempt(
        raw,
        normalizedOperation,
        requestFingerprint,
        nowMs,
      )
      : null;
  } catch {
    throw new PendingIdempotencyAttemptStorageUnavailableError();
  }
};

export const clearPendingIdempotencyAttempt = ({
  namespace,
  operation,
  requestFingerprint,
  expectedIdempotencyKey,
  storage: providedStorage,
}: ClearPendingAttemptOptions): void => {
  if (!requestFingerprint) {
    return;
  }
  const normalizedNamespace = normalizeRequiredIdentifier(namespace, 'namespace');
  const normalizedOperation = normalizeRequiredIdentifier(operation, 'operation');
  const storage = getStorage(providedStorage);
  if (!storage) {
    return;
  }
  const storageKey = buildAttemptStorageKey(
    normalizedNamespace,
    normalizedOperation,
    requestFingerprint,
  );
  let raw: string | null;
  try {
    raw = storage.getItem(storageKey);
  } catch {
    // A read failure cannot establish ownership of the stored attempt.
    return;
  }
  if (!raw) {
    return;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedPendingIdempotencyAttempt>;
    if (parsed.idempotencyKey !== expectedIdempotencyKey) {
      return;
    }
  } catch {
    // Corrupt state at this exact fingerprint has no usable owner.
  }
  removeStorageEntry(storage, storageKey);
};
